(() => {
  const root = document.documentElement;
  if (root.dataset.xonyraCartReady === 'true') return;
  root.dataset.xonyraCartReady = 'true';

  const routeRoot = window.Shopify?.routes?.root || '/';
  const sectionIds = ['xonyra-header', 'xonyra-cart-drawer'];
  let drawerTrigger = null;

  const money = (cents, format) => {
    const value = (Number(cents) / 100).toFixed(2);
    return format
      ? format
          .replace(/\{\{\s*amount\s*\}\}/, value)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(Number(cents) / 100))
      : `RM ${value}`;
  };

  const drawerRoot = () => document.querySelector('[data-cart-drawer-root]');
  const drawerPanel = () => document.querySelector('[data-cart-drawer-panel]');
  const drawerStatus = () => document.querySelector('[data-cart-drawer-status]');
  const message = (name, fallback) => drawerRoot()?.dataset[name] || fallback;

  const announce = (message) => {
    const status = drawerStatus();
    if (!status) return;
    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  };

  const openDrawer = (trigger, statusMessage = message('addedMessage', 'Added to cart')) => {
    const drawer = drawerRoot();
    const panel = drawerPanel();
    if (!drawer || !panel) return;
    drawerTrigger = trigger || drawerTrigger || document.activeElement;
    drawer.hidden = false;
    document.body.classList.add('xonyra-cart-drawer-open');
    window.requestAnimationFrame(() => {
      panel.focus();
      announce(statusMessage);
    });
  };

  const closeDrawer = () => {
    const drawer = drawerRoot();
    if (!drawer || drawer.hidden) return;
    drawer.hidden = true;
    document.body.classList.remove('xonyra-cart-drawer-open');
    if (drawerTrigger instanceof HTMLElement && document.contains(drawerTrigger)) {
      drawerTrigger.focus();
    }
  };

  const replaceSection = (sectionId, html) => {
    if (!html) return;
    const current = document.getElementById(`shopify-section-${sectionId}`);
    if (!current) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.getElementById(`shopify-section-${sectionId}`);
    if (replacement) current.replaceWith(replacement);
  };

  const applySections = (sections) => {
    if (!sections) return;
    sectionIds.forEach((sectionId) => replaceSection(sectionId, sections[sectionId]));
  };

  const refreshCountFallback = async () => {
    const response = await fetch(`${routeRoot}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart count request failed');
    const cart = await response.json();
    document.querySelectorAll('a[href$="/cart"]').forEach((link) => {
      link.setAttribute('aria-label', `Cart, ${cart.item_count} items`);
      link.textContent = cart.item_count > 0 ? `Cart (${cart.item_count})` : 'Cart';
    });
  };

  const sectionRequest = () => ({
    sections: sectionIds,
    sections_url: window.location.pathname
  });

  const add = async (id, quantity, button, feedback) => {
    if (!id || !button || button.disabled || button.dataset.loading === 'true') return;
    const original = button.textContent;
    button.dataset.loading = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${routeRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: Math.max(1, Number(quantity) || 1) }],
          ...sectionRequest()
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Cart request failed');
      applySections(result.sections);
      if (!result.sections?.['xonyra-header']) await refreshCountFallback();
      if (feedback) feedback.textContent = message('addedMessage', 'Added to cart');
      document.dispatchEvent(new CustomEvent('xonyra:cart-added', { detail: result }));
      openDrawer(button, message('addedMessage', 'Added to cart'));
    } catch (error) {
      if (feedback) {
        feedback.textContent =
          error.message || message('addErrorMessage', 'Could not add this item. Please try again.');
      }
    } finally {
      button.disabled = false;
      button.dataset.loading = 'false';
      button.removeAttribute('aria-busy');
      window.setTimeout(() => {
        button.textContent = original;
      }, 1400);
    }
  };

  const updateLine = async (lineKey, quantity, trigger) => {
    if (!lineKey || trigger?.dataset.loading === 'true') return;
    if (trigger) {
      trigger.dataset.loading = 'true';
      trigger.disabled = true;
      trigger.setAttribute('aria-busy', 'true');
    }

    try {
      const response = await fetch(`${routeRoot}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: Math.max(0, Number(quantity) || 0),
          ...sectionRequest()
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Cart update failed');
      applySections(result.sections);
      if (!result.sections?.['xonyra-header']) await refreshCountFallback();
      openDrawer(drawerTrigger, message('updatedMessage', 'Cart updated.'));
    } catch (error) {
      announce(
        error.message ||
          message('updateErrorMessage', 'Could not update the cart. Please try again.')
      );
    } finally {
      if (trigger && document.contains(trigger)) {
        trigger.disabled = false;
        trigger.dataset.loading = 'false';
        trigger.removeAttribute('aria-busy');
      }
    }
  };

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-cart-drawer-close]');
    if (closeButton) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (quantityButton) {
      event.preventDefault();
      updateLine(quantityButton.dataset.lineKey, quantityButton.dataset.quantity, quantityButton);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      event.preventDefault();
      updateLine(removeButton.dataset.lineKey, 0, removeButton);
      return;
    }

    const cardButton = event.target.closest('[data-card-add]');
    if (cardButton) {
      event.preventDefault();
      event.stopPropagation();
      add(
        cardButton.dataset.variantId,
        1,
        cardButton,
        cardButton.parentElement.querySelector('[data-card-feedback]')
      );
      return;
    }

    const stickyButton = event.target.closest('[data-sticky-add]');
    if (stickyButton) {
      event.preventDefault();
      const formId = stickyButton.getAttribute('form');
      const form = formId ? document.getElementById(formId) : null;
      const variantInput = form?.querySelector('[name="id"]');
      const quantityInput = form?.querySelector('[name="quantity"]');
      add(
        variantInput?.value,
        quantityInput?.value || 1,
        stickyButton,
        document.querySelector('[data-cart-feedback]')
      );
      return;
    }

    const preset = event.target.closest('[data-quantity]');
    if (preset) {
      const input = document.querySelector('.xonyra-product-form [name="quantity"]');
      if (!input) return;
      input.value = preset.dataset.quantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('keydown', (event) => {
    const drawer = drawerRoot();
    const panel = drawerPanel();
    if (!drawer || drawer.hidden || !panel) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = Array.from(
      panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const boundForms = new WeakSet();
  const bindProduct = (scope = document) => {
    const product =
      scope.querySelector?.('[data-xonyra-product]') ||
      document.querySelector('[data-xonyra-product]');
    const form = product?.querySelector('.xonyra-product-form');
    const sticky = document.querySelector('[data-sticky-cart]');
    if (!product || !form || boundForms.has(form)) return;
    boundForms.add(form);
    const quantityInput = form.querySelector('[name="quantity"]');

    const updateQuantity = () => {
      const quantity = Math.max(1, Number(quantityInput?.value || 1));
      product
        .querySelectorAll('[data-quantity]')
        .forEach((button) =>
          button.setAttribute(
            'aria-pressed',
            String(Number(button.dataset.quantity) === quantity)
          )
        );
      const subtotal = product.querySelector('[data-quantity-subtotal]');
      const price = Number(product.dataset.variantPrice || 0);
      if (subtotal && price) {
        subtotal.textContent = money(price * quantity, product.dataset.moneyFormat);
      }
    };

    quantityInput?.addEventListener('change', updateQuantity);
    quantityInput?.addEventListener('input', updateQuantity);

    const mainAdd = form.querySelector('[data-main-add]');
    if (sticky && mainAdd) {
      let stickyFrame = null;
      const updateStickyVisibility = () => {
        stickyFrame = null;
        const mainAddRect = mainAdd.getBoundingClientRect();
        sticky.hidden =
          mainAddRect.bottom >= 0 || sticky.dataset.available !== 'true';
      };
      const requestStickyUpdate = () => {
        if (stickyFrame !== null) return;
        stickyFrame = window.requestAnimationFrame(updateStickyVisibility);
      };
      const observer = new IntersectionObserver(requestStickyUpdate, { threshold: [0, 0.1, 1] });
      observer.observe(mainAdd);
      window.addEventListener('scroll', requestStickyUpdate, { passive: true });
      window.addEventListener('resize', requestStickyUpdate, { passive: true });
      requestStickyUpdate();
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.xonyra-product-form');
    if (!form) return;
    event.preventDefault();
    const variantInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const trigger = form.querySelector('[data-main-add]');
    const feedback = form.querySelector('[data-main-feedback]');
    add(
      variantInput?.value,
      quantityInput?.value || 1,
      trigger,
      feedback
    );
  });

  document.addEventListener('shopify:section:load', (event) => bindProduct(event.target));
  bindProduct();
})();
